import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Chip,
  Button, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Alert,
  TextField, Grid, Divider, MenuItem, Stack, Tooltip,
} from '@mui/material';
import {
  Add, Edit, Delete, Send, CheckCircle, Undo, Rule, Gavel, Flag, TrendingUp,
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = {
  Draft: 'default', ObjectivesSubmitted: 'info', ObjectivesApproved: 'info', InProgress: 'primary',
  SelfAppraised: 'warning', SupervisorReviewed: 'secondary', CommitteeReviewed: 'secondary',
  Completed: 'success', PIP: 'error',
};
const STATUS_LABEL = {
  Draft: 'Draft', ObjectivesSubmitted: 'Objectives Submitted', ObjectivesApproved: 'Objectives Approved',
  InProgress: 'In Progress', SelfAppraised: 'Self-Appraised', SupervisorReviewed: 'Supervisor Reviewed',
  CommitteeReviewed: 'Committee Reviewed', Completed: 'Completed', PIP: 'On PIP',
};

const emptyObjective = {
  title: '', description: '', kpi: '', weight: 0, agreedTarget: '',
  meansOfVerification: '', timeline: '', milestones: [],
};

// Scores are rated out of 100. The input shows the range and flags out-of-range entries.
const MIN_SCORE = 0;
const MAX_SCORE = 100;
const ScoreInput = ({ value, onChange }) => {
  const [v, setV] = useState(value ?? '');
  const num = Number(v);
  const out = v !== '' && (isNaN(num) || num < MIN_SCORE || num > MAX_SCORE);
  return (
    <TextField
      type="number" size="small" sx={{ width: 96 }}
      value={v}
      onChange={e => { setV(e.target.value); onChange(e.target.value); }}
      error={out}
      inputProps={{ min: MIN_SCORE, max: MAX_SCORE, step: 1 }}
      helperText={out ? `Must be ${MIN_SCORE}–${MAX_SCORE}` : `out of ${MAX_SCORE}`}
      FormHelperTextProps={{ sx: { m: 0, fontSize: 9, lineHeight: 1.2, textAlign: 'center' } }}
    />
  );
};

// Inline "add a milestone" row shown inside an objective's drilldown
const MilestoneAdder = ({ onAdd }) => {
  const [m, setM] = useState({ title: '', dueDate: '', status: 'Pending' });
  return (
    <Stack direction="row" spacing={1} alignItems="center" mt={1} sx={{ ml: 1 }}>
      <TextField label="New milestone" size="small" value={m.title} onChange={e => setM({ ...m, title: e.target.value })} sx={{ flex: 1 }} />
      <TextField type="date" size="small" InputLabelProps={{ shrink: true }} value={m.dueDate} onChange={e => setM({ ...m, dueDate: e.target.value })} />
      <TextField select size="small" value={m.status} onChange={e => setM({ ...m, status: e.target.value })} sx={{ width: 120 }}>
        {['Pending', 'InProgress', 'Achieved', 'Missed'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
      </TextField>
      <Button size="small" startIcon={<Add />} disabled={!m.title} onClick={() => { onAdd(m); setM({ title: '', dueDate: '', status: 'Pending' }); }}>Add</Button>
    </Stack>
  );
};

const AppraisalCard = ({ appraisalId, onClose, onChanged }) => {
  const [a, setA]       = useState(null);
  const [error, setErr] = useState('');
  const [mode, setMode] = useState('view');      // view | self | supervisor
  const [draft, setDraft] = useState({});        // {objId: {selfScore, selfAchievement, supervisorScore, supervisorVerifiedResult, remarks}}
  const [comp, setComp] = useState([]);          // competency score draft
  const [comment, setComment] = useState('');
  const [objForm, setObjForm] = useState(null);  // objective add/edit dialog
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [employees, setEmployees] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/appraisal/appraisals/${appraisalId}`, { headers: hdrs() });
      setA(r.data);
    } catch (e) { setErr(e.response?.data?.error || 'Failed to load'); }
  }, [appraisalId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/api/employees`, { headers: hdrs() }).then(r => setEmployees(r.data)).catch(() => {}); }, []);

  const setSupervisor = async (employeeNo) => {
    const sup = employees.find(e => e.employeeNo === employeeNo);
    await axios.put(`${API}/api/appraisal/appraisals/${a._id}`,
      { supervisorNo: employeeNo, supervisorName: sup ? `${sup.firstName} ${sup.lastName}` : '' }, { headers: hdrs() });
    refresh();
  };

  const refresh = () => { load(); onChanged && onChanged(); };

  const totalWeight = a ? a.objectives.reduce((s, o) => s + (Number(o.weight) || 0), 0) : 0;
  const canEditObjectives = a && ['Draft'].includes(a.status);

  // ── Objective CRUD (kept in the appraisal via PUT) ──
  const saveObjectives = async (objectives) => {
    setErr('');
    try {
      await axios.put(`${API}/api/appraisal/appraisals/${a._id}`, { objectives }, { headers: hdrs() });
      refresh();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save objectives'); }
  };
  const saveObjForm = async () => {
    const objs = [...a.objectives];
    const clean = { ...objForm, weight: Number(objForm.weight) || 0 };
    if (objForm._idx != null) objs[objForm._idx] = clean; else objs.push(clean);
    setObjForm(null);
    await saveObjectives(objs);
  };
  const removeObjective = async (idx) => { await saveObjectives(a.objectives.filter((_, i) => i !== idx)); };
  const addMilestone = async (objIdx, m) =>
    saveObjectives(a.objectives.map((o, i) => i === objIdx ? { ...o, milestones: [...(o.milestones || []), m] } : o));
  const removeMilestone = async (objIdx, mIdx) =>
    saveObjectives(a.objectives.map((o, i) => i === objIdx ? { ...o, milestones: (o.milestones || []).filter((_, j) => j !== mIdx) } : o));

  // ── Workflow actions ──
  const act = async (path, body = {}) => {
    setBusy(true); setErr('');
    try { await axios.post(`${API}/api/appraisal/appraisals/${a._id}/${path}`, body, { headers: hdrs() }); setMode('view'); setComment(''); refresh(); }
    catch (e) { setErr(e.response?.data?.error || 'Action failed'); }
    finally { setBusy(false); }
  };

  // Effective value = what the user typed (draft), else what was previously saved
  const isBlank = (v) => v === undefined || v === null || v === '';
  const objEff  = (o, field) => (isBlank(draft[o._id]?.[field]) ? o[field] : draft[o._id][field]);
  const compEff = (c, i, field) => (isBlank(comp[i]?.[field]) ? c[field] : comp[i][field]);
  const badScore = (v) => isBlank(v) || isNaN(Number(v)) || Number(v) < 0 || Number(v) > 100;

  // Every objective AND every competency must carry a 0–100 rating — no blanks.
  const validateAllScored = (field) => {
    for (const o of a.objectives)
      if (badScore(objEff(o, field))) return `Enter a 0–100 rating for objective “${o.title}”.`;
    for (let i = 0; i < a.competencies.length; i++)
      if (badScore(compEff(a.competencies[i], i, field))) return `Enter a 0–100 rating for competency “${a.competencies[i].name}”.`;
    return null;
  };

  const submitSelf = () => {
    const v = validateAllScored('selfScore');
    if (v) { setErr(v); return; }
    act('self-appraise', {
      objectives: a.objectives.map(o => ({ _id: o._id, selfScore: Number(objEff(o, 'selfScore')), selfAchievement: draft[o._id]?.selfAchievement ?? o.selfAchievement ?? '' })),
      competencies: a.competencies.map((c, i) => ({ selfScore: Number(compEff(c, i, 'selfScore')) })),
      comment,
    });
  };
  const submitSupervisor = () => {
    const v = validateAllScored('supervisorScore');
    if (v) { setErr(v); return; }
    act('supervisor-review', {
      objectives: a.objectives.map(o => ({ _id: o._id, supervisorScore: Number(objEff(o, 'supervisorScore')), supervisorVerifiedResult: draft[o._id]?.supervisorVerifiedResult ?? o.supervisorVerifiedResult ?? '', remarks: draft[o._id]?.remarks ?? o.remarks ?? '' })),
      competencies: a.competencies.map((c, i) => ({ supervisorScore: Number(compEff(c, i, 'supervisorScore')) })),
      comment,
    });
  };

  const setD = (id, k, v) => setDraft(p => ({ ...p, [id]: { ...p[id], [k]: v } }));

  if (!a) return (
    <Dialog open fullWidth maxWidth="md"><DialogContent>{error ? <Alert severity="error">{error}</Alert> : 'Loading…'}</DialogContent></Dialog>
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6" component="div" fontWeight={700}>{a.employeeName} <Typography component="span" variant="body2" color="text.secondary">({a.employeeNo})</Typography></Typography>
            <Typography variant="caption" component="div" color="text.secondary">{a.appraisalNo} · {a.periodCode} · {a.department || '—'} · Supervisor: {a.supervisorName || a.supervisorNo || '—'}</Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Chip label={STATUS_LABEL[a.status]} color={STATUS_COLOR[a.status] || 'default'} size="small" />
            {a.selfOverall != null && <Chip label={`Self ${a.selfOverall}%`} size="small" variant="outlined" />}
            {a.supervisorOverall != null && <Chip label={`Supervisor ${a.supervisorOverall}%`} size="small" variant="outlined" color="secondary" />}
            {a.finalScore != null && <Chip label={`Final ${a.finalScore}% — ${a.finalRating}`} size="small" color="success" />}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {a.pipRef && <Alert severity="warning" sx={{ mb: 2 }}>Placed on Performance Improvement Plan <strong>{a.pipRef}</strong> (reviewed monthly).</Alert>}

        {/* Supervisor — assignable while drafting; approves goals & verifies results */}
        {canEditObjectives ? (
          <Box mb={2}>
            <TextField select label="Supervisor (approves goals & verifies results)" value={a.supervisorNo || ''}
              onChange={e => setSupervisor(e.target.value)} size="small" sx={{ minWidth: 360 }}>
              <MenuItem value=""><em>— None —</em></MenuItem>
              {employees.filter(e => e.employeeNo !== a.employeeNo).map(e => (
                <MenuItem key={e.employeeNo} value={e.employeeNo}>{e.employeeNo} — {e.firstName} {e.lastName}{e.jobTitle ? ` · ${e.jobTitle}` : ''}</MenuItem>
              ))}
            </TextField>
          </Box>
        ) : (
          <Box mb={2} sx={{ p: 1.5, bgcolor: '#f5f7fb', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">Supervisor</Typography>
            <Typography variant="body2" fontWeight={600}>{a.supervisorName || a.supervisorNo || '— not assigned —'}</Typography>
          </Box>
        )}

        {/* Objectives */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1" fontWeight={700}>Objectives & Targets</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label={`Total weight ${totalWeight}%`} size="small" color={totalWeight === 100 ? 'success' : 'warning'} variant="outlined" />
            {canEditObjectives && <Button size="small" startIcon={<Add />} onClick={() => setObjForm({ ...emptyObjective })}>Add</Button>}
          </Box>
        </Box>

        <Table size="small">
          <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell><strong>Objective / KPI</strong></TableCell>
            <TableCell align="center"><strong>Weight</strong></TableCell>
            <TableCell><strong>Agreed Target</strong></TableCell>
            <TableCell align="center"><strong>Milestones</strong></TableCell>
            {(mode !== 'view' || a.selfOverall != null) && <TableCell align="center"><strong>Self</strong></TableCell>}
            {(mode === 'supervisor' || a.supervisorOverall != null) && <TableCell align="center"><strong>Supervisor</strong></TableCell>}
            {canEditObjectives && <TableCell align="right"></TableCell>}
          </TableRow></TableHead>
          <TableBody>
            {a.objectives.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary" py={2}>No objectives set.</Typography></TableCell></TableRow>
            ) : a.objectives.map((o, idx) => (
              <React.Fragment key={o._id || idx}>
                <TableRow hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{o.title}</Typography>
                    {o.kpi && <Typography variant="caption" color="text.secondary">KPI: {o.kpi}</Typography>}
                  </TableCell>
                  <TableCell align="center">{o.weight}%</TableCell>
                  <TableCell><Typography variant="caption">{o.agreedTarget || '—'}</Typography></TableCell>
                  <TableCell align="center">
                    <Tooltip title={(o.milestones?.length || 0) > 0 ? 'View milestones' : 'No milestones'}>
                      <Chip
                        size="small"
                        label={o.milestones?.length || 0}
                        variant={expanded === idx ? 'filled' : 'outlined'}
                        color={(o.milestones?.length || 0) > 0 ? 'primary' : 'default'}
                        onClick={() => setExpanded(expanded === idx ? null : idx)}
                        sx={{ cursor: 'pointer', minWidth: 38 }}
                      />
                    </Tooltip>
                  </TableCell>
                  {(mode !== 'view' || a.selfOverall != null) && (
                    <TableCell align="center">
                      {mode === 'self'
                        ? <ScoreInput value={o.selfScore} onChange={val => setD(o._id, 'selfScore', val)} />
                        : (o.selfScore ?? '—')}
                    </TableCell>
                  )}
                  {(mode === 'supervisor' || a.supervisorOverall != null) && (
                    <TableCell align="center">
                      {mode === 'supervisor'
                        ? <ScoreInput value={o.supervisorScore} onChange={val => setD(o._id, 'supervisorScore', val)} />
                        : (o.supervisorScore ?? '—')}
                    </TableCell>
                  )}
                  {canEditObjectives && (
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setObjForm({ ...o, _idx: idx })}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => removeObjective(idx)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
                {expanded === idx && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ bgcolor: '#fafafa' }}>
                      <Grid container spacing={1} sx={{ py: 1 }}>
                        {o.description && <Grid item xs={12}><Typography variant="caption"><strong>Description:</strong> {o.description}</Typography></Grid>}
                        <Grid item xs={6}><Typography variant="caption"><strong>Means of Verification:</strong> {o.meansOfVerification || '—'}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption"><strong>Timeline:</strong> {o.timeline || '—'}</Typography></Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" fontWeight={700}>Milestones:</Typography>
                          {(o.milestones || []).map((m, i) => (
                            <Box key={i} display="flex" alignItems="center" gap={0.5} sx={{ ml: 1 }}>
                              <Typography variant="caption">• {m.title} {m.dueDate ? `(due ${new Date(m.dueDate).toLocaleDateString()})` : ''} — {m.status}</Typography>
                              {canEditObjectives && <IconButton size="small" onClick={() => removeMilestone(idx, i)}><Delete sx={{ fontSize: 14 }} /></IconButton>}
                            </Box>
                          ))}
                          {(o.milestones || []).length === 0 && <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 1 }}>None yet.</Typography>}
                          {canEditObjectives && <MilestoneAdder onAdd={m => addMilestone(idx, m)} />}
                        </Grid>
                        {mode === 'self' && <Grid item xs={12}><TextField label="My achievement / evidence" size="small" fullWidth defaultValue={o.selfAchievement} onChange={e => setD(o._id, 'selfAchievement', e.target.value)} /></Grid>}
                        {o.selfAchievement && mode !== 'self' && <Grid item xs={12}><Typography variant="caption"><strong>Self achievement:</strong> {o.selfAchievement}</Typography></Grid>}
                        {mode === 'supervisor' && <>
                          <Grid item xs={12}><TextField label="Verified result" size="small" fullWidth defaultValue={o.supervisorVerifiedResult} onChange={e => setD(o._id, 'supervisorVerifiedResult', e.target.value)} /></Grid>
                          <Grid item xs={12}><TextField label="Remarks" size="small" fullWidth defaultValue={o.remarks} onChange={e => setD(o._id, 'remarks', e.target.value)} /></Grid>
                        </>}
                        {o.supervisorVerifiedResult && mode !== 'supervisor' && <Grid item xs={12}><Typography variant="caption"><strong>Verified result:</strong> {o.supervisorVerifiedResult}</Typography></Grid>}
                        {a.status === 'InProgress' && (
                          <Grid item xs={12}>
                            <ProgressLogger appraisalId={a._id} objectiveId={o._id} onLogged={refresh} />
                            {o.progressUpdates?.length > 0 && o.progressUpdates.map((p, i) => (
                              <Typography key={i} variant="caption" display="block" color="text.secondary" sx={{ ml: 1 }}>↳ {new Date(p.date).toLocaleDateString()}: {p.percent}% — {p.note}</Typography>
                            ))}
                          </Grid>
                        )}
                      </Grid>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>

        {/* Competencies */}
        {a.competencies?.length > 0 && (
          <>
            <Box display="flex" alignItems="baseline" gap={1} mt={3} mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>Competencies</Typography>
              <Typography variant="caption" color="text.secondary">rated {MIN_SCORE}–{MAX_SCORE}</Typography>
            </Box>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Competency</strong></TableCell>
                {(mode !== 'view' || a.selfOverall != null) && <TableCell align="center"><strong>Self ({MIN_SCORE}–{MAX_SCORE})</strong></TableCell>}
                {(mode === 'supervisor' || a.supervisorOverall != null) && <TableCell align="center"><strong>Supervisor ({MIN_SCORE}–{MAX_SCORE})</strong></TableCell>}
              </TableRow></TableHead>
              <TableBody>
                {a.competencies.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell>{c.name}</TableCell>
                    {(mode !== 'view' || a.selfOverall != null) && <TableCell align="center">{mode === 'self' ? <ScoreInput value={c.selfScore} onChange={val => setComp(p => { const n = [...p]; n[i] = { ...n[i], selfScore: val }; return n; })} /> : (c.selfScore ?? '—')}</TableCell>}
                    {(mode === 'supervisor' || a.supervisorOverall != null) && <TableCell align="center">{mode === 'supervisor' ? <ScoreInput value={c.supervisorScore} onChange={val => setComp(p => { const n = [...p]; n[i] = { ...n[i], supervisorScore: val }; return n; })} /> : (c.supervisorScore ?? '—')}</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {mode !== 'view' && (
          <TextField label="Overall comment" fullWidth size="small" multiline rows={2} sx={{ mt: 2 }} value={comment} onChange={e => setComment(e.target.value)} />
        )}

        {/* Comments trail */}
        {(a.comments?.self || a.comments?.supervisor || a.comments?.committee || a.comments?.hrm) && mode === 'view' && (
          <Box mt={2}>
            <Divider sx={{ mb: 1 }} />
            {a.comments.self && <Typography variant="caption" display="block"><strong>Employee:</strong> {a.comments.self}</Typography>}
            {a.comments.supervisor && <Typography variant="caption" display="block"><strong>Supervisor:</strong> {a.comments.supervisor}</Typography>}
            {a.comments.committee && <Typography variant="caption" display="block"><strong>Committee:</strong> {a.comments.committee}</Typography>}
            {a.comments.hrm && <Typography variant="caption" display="block"><strong>HRM:</strong> {a.comments.hrm}</Typography>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5, px: 2 }}>
        {/* Stage-aware action bar */}
        {mode === 'view' && <>
          {a.status === 'Draft' && <Button variant="contained" startIcon={<Send />} disabled={busy || totalWeight !== 100} onClick={() => act('submit-objectives')}>Submit Objectives</Button>}
          {a.status === 'ObjectivesSubmitted' && <>
            <Button variant="contained" color="success" startIcon={<CheckCircle />} disabled={busy} onClick={() => act('approve-objectives', { decision: 'Approved' })}>Approve Goals</Button>
            <Button variant="outlined" color="warning" startIcon={<Undo />} disabled={busy} onClick={() => act('approve-objectives', { decision: 'Returned', comment: 'Returned for revision' })}>Return</Button>
            <Button variant="text" startIcon={<Edit />} disabled={busy} onClick={() => act('reopen-objectives')}>Reopen to Edit</Button>
          </>}
          {(a.status === 'InProgress' || a.status === 'ObjectivesApproved') && <Button variant="contained" startIcon={<Rule />} onClick={() => { setMode('self'); setDraft({}); setComp([]); }}>Start Self-Appraisal</Button>}
          {a.status === 'SelfAppraised' && <Button variant="contained" color="secondary" startIcon={<Rule />} onClick={() => { setMode('supervisor'); setDraft({}); setComp([]); }}>Supervisor Review</Button>}
          {a.status === 'SupervisorReviewed' && <>
            <Button variant="outlined" startIcon={<Gavel />} disabled={busy} onClick={() => act('committee-review', { comment: 'Moderated by committee' })}>Committee Review</Button>
            <Button variant="contained" color="success" startIcon={<Flag />} disabled={busy} onClick={() => act('finalize')}>Finalize</Button>
          </>}
          {a.status === 'CommitteeReviewed' && <Button variant="contained" color="success" startIcon={<Flag />} disabled={busy} onClick={() => act('finalize')}>Finalize</Button>}
          <Box flex={1} />
          <Button onClick={onClose}>Close</Button>
        </>}
        {mode === 'self' && <>
          <Button onClick={() => setMode('view')}>Cancel</Button><Box flex={1} />
          <Button variant="contained" disabled={busy} onClick={submitSelf}>Submit Self-Appraisal</Button>
        </>}
        {mode === 'supervisor' && <>
          <Button onClick={() => setMode('view')}>Cancel</Button><Box flex={1} />
          <Button variant="contained" color="secondary" disabled={busy} onClick={submitSupervisor}>Submit Review</Button>
        </>}
      </DialogActions>

      {/* Objective add/edit dialog */}
      {objForm && <ObjectiveForm value={objForm} onChange={setObjForm} onSave={saveObjForm} onClose={() => setObjForm(null)} />}
    </Dialog>
  );
};

// ── Inline progress logger ──
const ProgressLogger = ({ appraisalId, objectiveId, onLogged }) => {
  const [percent, setPercent] = useState('');
  const [note, setNote] = useState('');
  const log = async () => {
    await axios.post(`${API}/api/appraisal/appraisals/${appraisalId}/progress`, { objectiveId, percent: Number(percent) || 0, note }, { headers: hdrs() });
    setPercent(''); setNote(''); onLogged();
  };
  return (
    <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
      <TextField type="number" label="%" size="small" sx={{ width: 70 }} value={percent} onChange={e => setPercent(e.target.value)} />
      <TextField label="Progress note" size="small" sx={{ flex: 1 }} value={note} onChange={e => setNote(e.target.value)} />
      <Button size="small" startIcon={<TrendingUp />} onClick={log} disabled={!note}>Log</Button>
    </Stack>
  );
};

// ── Objective form (with milestones) ──
const ObjectiveForm = ({ value, onChange, onSave, onClose }) => {
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: true, value, onClose });
  const set = (k, v) => onChange({ ...value, [k]: v });
  const addMilestone = () => set('milestones', [...(value.milestones || []), { title: '', dueDate: '', status: 'Pending' }]);
  const setMs = (i, k, v) => { const m = [...value.milestones]; m[i] = { ...m[i], [k]: v }; set('milestones', m); };
  const wNum = Number(value.weight);
  const weightBad = value.weight === '' || value.weight === null || isNaN(wNum) || wNum < 0 || wNum > 100;
  return (
    <Dialog open onClose={attemptClose} fullWidth maxWidth="sm">
      <DialogTitle>{value._idx != null ? 'Edit Objective' : 'Add Objective'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}><TextField label="Objective Title *" value={value.title} onChange={e => set('title', e.target.value)} fullWidth size="small" /></Grid>
          <Grid item xs={12}><TextField label="Description" value={value.description} onChange={e => set('description', e.target.value)} fullWidth size="small" multiline rows={2} /></Grid>
          <Grid item xs={8}><TextField label="KPI / Performance Indicator" value={value.kpi} onChange={e => set('kpi', e.target.value)} fullWidth size="small" /></Grid>
          <Grid item xs={4}><TextField type="number" label="Weight % *" value={value.weight} onChange={e => set('weight', e.target.value)} fullWidth size="small"
            error={weightBad} helperText={weightBad ? 'Must be 0–100' : ' '} inputProps={{ min: 0, max: 100, step: 1 }} /></Grid>
          <Grid item xs={12}><TextField label="Agreed Target" value={value.agreedTarget} onChange={e => set('agreedTarget', e.target.value)} fullWidth size="small" /></Grid>
          <Grid item xs={6}><TextField label="Means of Verification" value={value.meansOfVerification} onChange={e => set('meansOfVerification', e.target.value)} fullWidth size="small" /></Grid>
          <Grid item xs={6}><TextField label="Timeline" value={value.timeline} onChange={e => set('timeline', e.target.value)} fullWidth size="small" placeholder="e.g. Q1–Q4" /></Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" fontWeight={700}>Milestones</Typography>
              <Button size="small" startIcon={<Add />} onClick={addMilestone}>Add milestone</Button>
            </Box>
            {(value.milestones || []).map((m, i) => (
              <Stack direction="row" spacing={1} key={i} mt={0.5}>
                <TextField label="Milestone" size="small" sx={{ flex: 1 }} value={m.title} onChange={e => setMs(i, 'title', e.target.value)} />
                <TextField type="date" size="small" InputLabelProps={{ shrink: true }} value={m.dueDate?.slice(0, 10) || ''} onChange={e => setMs(i, 'dueDate', e.target.value)} />
                <TextField select size="small" sx={{ width: 120 }} value={m.status} onChange={e => setMs(i, 'status', e.target.value)}>
                  {['Pending', 'InProgress', 'Achieved', 'Missed'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Stack>
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions><Button onClick={attemptClose}>Cancel</Button><Button variant="contained" disabled={!value.title || weightBad} onClick={onSave}>Save Objective</Button></DialogActions>
      {discardDialog}
    </Dialog>
  );
};

export default AppraisalCard;
